import { useState } from 'react';

import type {
  MarketplaceDwellingType,
  MarketplaceListingFiltersInput,
  MarketplaceSearchAreaInput,
} from '../browse/__generated__/marketplace-browse.zeus';
import type { MarketplaceBrowseVariables } from '../browse/marketplaceBrowse';
import { dollarsToCents } from './money';
import { DEFAULT_LISTING_SORT_SLUG, listingSortKeys, type ListingSortSlug } from './sortOptions';
import {
  isUuid,
  normalizeSearchQuery,
  normalizeSearchVertices,
  type PropertySearchQuery,
} from './useSearchQueries';

type SearchLocation = Pick<PropertySearchQuery, 'market' | 'zoneIds' | 'vertices'>;

const isInRange = (
  value: number | null | undefined,
  minimum: number,
  maximum: number,
): value is number =>
  value != null && Number.isFinite(value) && value >= minimum && value <= maximum;

/**
 * Builds a search payload directly from a canonical query rather than from the
 * hook's live state. `useSearchState`'s state setters are async, so callers
 * that want to fetch with values they're applying in the same tick (e.g.
 * switching to a saved search) must build the payload from those values
 * explicitly instead of relying on `buildSearchPayload`.
 */
export const buildPayloadFromQuery = (
  query: PropertySearchQuery,
  location: SearchLocation,
): MarketplaceBrowseVariables => {
  const normalizedQuery = normalizeSearchQuery(query);
  const dwellingTypes: MarketplaceDwellingType[] = [];
  if (normalizedQuery.singleFamily) {
    dwellingTypes.push('HOUSE');
  }
  if (normalizedQuery.condo) {
    dwellingTypes.push('CONDO');
  }
  if (normalizedQuery.townhome) {
    dwellingTypes.push('TOWNHOUSE');
  }

  const filters: MarketplaceListingFiltersInput = {
    ...(normalizedQuery.canSelfTour ? { canSelfTour: true } : {}),
    ...(dwellingTypes.length === 1 || dwellingTypes.length === 2 ? { dwellingTypes } : {}),
    ...(normalizedQuery.hasPool ? { hasPool: true } : {}),
    ...(normalizedQuery.noHoas ? { maxHoaMonthlyFeeCents: 0 } : {}),
    ...(isInRange(normalizedQuery.maxListPrice, 1, 100_000_000)
      ? { maxListPriceCents: dollarsToCents(normalizedQuery.maxListPrice) }
      : {}),
    ...(isInRange(normalizedQuery.maxLotSize, 1, 10_000_000)
      ? { maxLotSquareFootage: normalizedQuery.maxLotSize }
      : {}),
    ...(isInRange(normalizedQuery.maxSquareFootage, 1, 100_000)
      ? { maxSquareFootage: normalizedQuery.maxSquareFootage }
      : {}),
    ...(isInRange(normalizedQuery.maxYearBuilt, 1600, 3000)
      ? { maxYearBuilt: normalizedQuery.maxYearBuilt }
      : {}),
    ...(isInRange(normalizedQuery.bathrooms, 0, 100) && normalizedQuery.bathrooms !== 0
      ? { minBathrooms: normalizedQuery.bathrooms }
      : {}),
    ...(isInRange(normalizedQuery.bedrooms, 1, 100)
      ? { minBedrooms: normalizedQuery.bedrooms }
      : {}),
    ...(isInRange(normalizedQuery.minListPrice, 1, 100_000_000)
      ? { minListPriceCents: dollarsToCents(normalizedQuery.minListPrice) }
      : {}),
    ...(isInRange(normalizedQuery.minLotSize, 1, 10_000_000)
      ? { minLotSquareFootage: normalizedQuery.minLotSize }
      : {}),
    ...(isInRange(normalizedQuery.minSquareFootage, 1, 100_000)
      ? { minSquareFootage: normalizedQuery.minSquareFootage }
      : {}),
    ...(isInRange(normalizedQuery.minYearBuilt, 1600, 3000)
      ? { minYearBuilt: normalizedQuery.minYearBuilt }
      : {}),
    ...(normalizedQuery.hasSingleStory ? { singleStoryOnly: true } : {}),
  };

  const { vertices, zoneIds, market } = location;
  const validVertices = normalizeSearchVertices(vertices);
  const validZoneIds = [...new Set(zoneIds?.filter(isUuid))].slice(0, 128);
  let where: MarketplaceSearchAreaInput | undefined;
  if (validVertices) {
    where = { vertices: validVertices };
  } else if (validZoneIds.length) {
    where = { zoneIds: validZoneIds };
  } else if (market?.trim() && market.trim().length <= 64) {
    where = { market: market.trim() };
  }

  const mapInput: MarketplaceBrowseVariables['mapInput'] = where
    ? { filters, mode: { markers: { area: where } } }
    : undefined;

  return {
    listInput: {
      filters,
      first: 30,
      page: 0,
      photoLimit: 1,
      sortBy: [...listingSortKeys(normalizedQuery.sort)],
      ...(where ? { where } : {}),
    },
    ...(mapInput ? { mapInput } : {}),
  };
};

export const getInitialPropertyTypes = (queryParams: PropertySearchQuery) => {
  const hasAnyPropertyTypeParam =
    queryParams.singleFamily !== undefined ||
    queryParams.condo !== undefined ||
    queryParams.townhome !== undefined;

  if (hasAnyPropertyTypeParam) {
    const propertyTypes = {
      singleFamily: queryParams.singleFamily ?? false,
      condo: queryParams.condo ?? false,
      townhome: queryParams.townhome ?? false,
    };
    if (propertyTypes.singleFamily || propertyTypes.condo || propertyTypes.townhome) {
      return propertyTypes;
    }
  }

  return { singleFamily: true, condo: true, townhome: true };
};

export const useSearchState = (queryParams: PropertySearchQuery) => {
  const normalizedQueryParams = normalizeSearchQuery(queryParams);
  const initialPropertyTypes = getInitialPropertyTypes(normalizedQueryParams);
  const [minBeds, setBedrooms] = useState(normalizedQueryParams.bedrooms || 0);
  const [minBaths, setBathrooms] = useState(normalizedQueryParams.bathrooms || 0);
  const [canSelfTour, setCanSelfTour] = useState<boolean | null>(
    normalizedQueryParams.canSelfTour ?? null,
  );
  const [minPrice, setMinPrice] = useState(normalizedQueryParams.minListPrice || null);
  const [maxPrice, setMaxPrice] = useState(normalizedQueryParams.maxListPrice || null);
  const [singleFamily, setSingleFamily] = useState(initialPropertyTypes.singleFamily);
  const [condo, setCondo] = useState(initialPropertyTypes.condo);
  const [townhome, setTownhome] = useState(initialPropertyTypes.townhome);
  const [noHoas, setNoHoas] = useState(normalizedQueryParams.noHoas || false);
  const [singleStory, setSingleStory] = useState(normalizedQueryParams.hasSingleStory || false);
  const [mustHavePool, setMustHavePool] = useState(normalizedQueryParams.hasPool || false);
  const [minYearBuilt, setMinYearBuilt] = useState(normalizedQueryParams.minYearBuilt || null);
  const [maxYearBuilt, setMaxYearBuilt] = useState(normalizedQueryParams.maxYearBuilt || null);
  const [minLotSize, setMinLotSize] = useState(normalizedQueryParams.minLotSize || null);
  const [maxLotSize, setMaxLotSize] = useState(normalizedQueryParams.maxLotSize || null);
  const [minSquareFootage, setMinSquareFootage] = useState(
    normalizedQueryParams.minSquareFootage || null,
  );
  const [maxSquareFootage, setMaxSquareFootage] = useState(
    normalizedQueryParams.maxSquareFootage || null,
  );
  const [sort, setSort] = useState<ListingSortSlug>(
    normalizedQueryParams.sort ?? DEFAULT_LISTING_SORT_SLUG,
  );

  // Replaces every filter value at once from a canonical query (e.g. when a
  // saved search is applied). Property types follow the same all-or-nothing
  // convention as initial hydration. Sort is a view preference rather than a
  // filter, so it deliberately survives both this and `resetFilters`.
  const applyCanonical = (query: PropertySearchQuery) => {
    const normalizedQuery = normalizeSearchQuery(query);
    const propertyTypes = getInitialPropertyTypes(normalizedQuery);
    setBedrooms(normalizedQuery.bedrooms || 0);
    setBathrooms(normalizedQuery.bathrooms || 0);
    setCanSelfTour(normalizedQuery.canSelfTour ?? null);
    setMinPrice(normalizedQuery.minListPrice || null);
    setMaxPrice(normalizedQuery.maxListPrice || null);
    setSingleFamily(propertyTypes.singleFamily);
    setCondo(propertyTypes.condo);
    setTownhome(propertyTypes.townhome);
    setNoHoas(normalizedQuery.noHoas || false);
    setSingleStory(normalizedQuery.hasSingleStory || false);
    setMustHavePool(normalizedQuery.hasPool || false);
    setMinYearBuilt(normalizedQuery.minYearBuilt || null);
    setMaxYearBuilt(normalizedQuery.maxYearBuilt || null);
    setMinLotSize(normalizedQuery.minLotSize || null);
    setMaxLotSize(normalizedQuery.maxLotSize || null);
    setMinSquareFootage(normalizedQuery.minSquareFootage || null);
    setMaxSquareFootage(normalizedQuery.maxSquareFootage || null);
  };

  const resetFilters = () => {
    setBathrooms(0);
    setBedrooms(0);
    setCanSelfTour(null);
    setCondo(true);
    setMaxLotSize(null);
    setMaxPrice(null);
    setMaxSquareFootage(null);
    setMaxYearBuilt(null);
    setMinLotSize(null);
    setMinPrice(null);
    setMinSquareFootage(null);
    setMinYearBuilt(null);
    setMustHavePool(false);
    setNoHoas(false);
    setSingleFamily(true);
    setSingleStory(false);
    setTownhome(true);
  };

  const getCanonical = (): PropertySearchQuery => ({
    bedrooms: minBeds,
    bathrooms: minBaths,
    canSelfTour,
    minListPrice: minPrice,
    maxListPrice: maxPrice,
    singleFamily,
    condo,
    townhome,
    noHoas,
    hasSingleStory: singleStory,
    hasPool: mustHavePool,
    minYearBuilt,
    maxYearBuilt,
    minLotSize,
    maxLotSize,
    minSquareFootage,
    maxSquareFootage,
    sort,
  });

  // Builds a payload from current filter state, with optional overrides for
  // values being changed in the same tick (which the async state setters
  // wouldn't reflect yet) and for the location.
  const buildSearchPayload = (
    override: Partial<PropertySearchQuery> = {},
  ): MarketplaceBrowseVariables =>
    buildPayloadFromQuery({ ...getCanonical(), ...override }, override);

  return {
    resetFilters,
    applyCanonical,
    buildSearchPayload,
    getCanonical,
    get: {
      minBeds,
      minBaths,
      canSelfTour,
      minPrice,
      maxPrice,
      singleFamily,
      condo,
      townhome,
      noHoas,
      singleStory,
      mustHavePool,
      minYearBuilt,
      maxYearBuilt,
      minLotSize,
      maxLotSize,
      minSquareFootage,
      maxSquareFootage,
      sort,
    },
    set: {
      setBedrooms,
      setBathrooms,
      setCanSelfTour,
      setMinPrice,
      setMaxPrice,
      setSingleFamily,
      setCondo,
      setTownhome,
      setNoHoas,
      setSingleStory,
      setMustHavePool,
      setMinYearBuilt,
      setMaxYearBuilt,
      setMinLotSize,
      setMaxLotSize,
      setMinSquareFootage,
      setMaxSquareFootage,
      setSort,
    },
  };
};
